import { useEffect, useMemo, useState } from "react";
import Box from "@mui/material/Box";
import Chip from "@mui/material/Chip";
import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";
import MenuItem from "@mui/material/MenuItem";
import TextField from "@mui/material/TextField";
import Paper from "@mui/material/Paper";
import Tooltip from "@mui/material/Tooltip";
import Pagination from "@mui/material/Pagination";
import { type GridColDef } from "@mui/x-data-grid";
import VisibilityOutlinedIcon from "@mui/icons-material/VisibilityOutlined";
import DeleteSweepOutlinedIcon from "@mui/icons-material/DeleteSweepOutlined";
import RefreshIcon from "@mui/icons-material/Refresh";
import { api } from "../lib/api";
import { useAsync } from "../lib/hooks";
import { useI18n } from "../lib/i18n";
import type { AdminLog } from "../lib/types";
import Button from "@mui/material/Button";
import { ErrorBox, Modal } from "../components/ui";
import { DataTable } from "../components/DataTable";

function getActionSummary(
  log: { action: string; summary?: string | null },
  t: (key: any) => string
): string {
  if (!log.action) return "—";
  const actionKey = `action.${log.action.replace(/:/g, ".")}`;
  const translated = t(actionKey);
  if (translated !== actionKey) {
    return translated;
  }
  return log.summary || log.action;
}

export function AdminLogs() {
  const { t, fmtDate } = useI18n();

  // Search & Filter state
  const [page, setPage] = useState<number>(1);
  const [limit] = useState<number>(20);
  const [search, setSearch] = useState<string>("");
  const [username, setUsername] = useState<string>("");
  const [ip, setIp] = useState<string>("");
  const [action, setAction] = useState<string>("");
  const [status, setStatus] = useState<"" | "SUCCESS" | "FAILED">("");
  const [method, setMethod] = useState<string>("");

  // Active query filters applied to API
  const [appliedFilters, setAppliedFilters] = useState({
    page: 1,
    search: "",
    username: "",
    ip: "",
    action: "",
    status: "" as "" | "SUCCESS" | "FAILED",
    method: "",
  });

  // Modal detail state
  const [selectedLog, setSelectedLog] = useState<AdminLog | null>(null);

  // Load distinct actions for dropdown
  const { data: actionsData } = useAsync(() => api.adminLogs.actions(), []);

  // Load logs
  const {
    data: logsData,
    loading: logsLoading,
    error: logsError,
    reload: reloadLogs,
  } = useAsync(
    () =>
      api.adminLogs.list({
        page: appliedFilters.page,
        limit,
        search: appliedFilters.search || undefined,
        username: appliedFilters.username || undefined,
        ip: appliedFilters.ip || undefined,
        action: appliedFilters.action || undefined,
        status: appliedFilters.status || undefined,
        method: appliedFilters.method || undefined,
      }),
    [appliedFilters]
  );

  const handleApplyFilter = () => {
    setPage(1);
    setAppliedFilters({
      page: 1,
      search,
      username,
      ip,
      action,
      status,
      method,
    });
  };

  const handlePageChange = (_: any, newPage: number) => {
    setPage(newPage);
    setAppliedFilters((prev) => ({ ...prev, page: newPage }));
  };

  const handleCleanLogs = async () => {
    const daysStr = prompt(t("adminlogs.cleanConfirm"), "30");
    if (!daysStr) return;
    const days = Number(daysStr);
    if (isNaN(days) || days < 1) {
      alert(t("adminlogs.invalidDays"));
      return;
    }

    try {
      const res = await api.adminLogs.clean(days);
      alert(t("adminlogs.cleanSuccess", { n: res.deleted }));
      reloadLogs();
    } catch (e: any) {
      alert(e.message);
    }
  };

  const columns = useMemo<GridColDef<AdminLog>[]>(
    () => [
      {
        field: "id",
        headerName: "ID",
        width: 75,
        renderCell: ({ value }) => (
          <span className="mono muted">#{value}</span>
        ),
      },
      {
        field: "createdAt",
        headerName: t("adminlogs.time"),
        width: 160,
        renderCell: ({ value }) => (
          <span style={{ fontSize: 13 }}>{fmtDate(value)}</span>
        ),
      },
      {
        field: "username",
        headerName: t("adminlogs.user"),
        width: 140,
        renderCell: ({ row }) => (
          <Stack spacing={0.2}>
            <Typography variant="body2" sx={{ fontWeight: 600 }}>
              {row.username || "—"}
            </Typography>
            <Typography variant="caption" className="muted">
              {row.role || "system"}{row.country ? ` [${row.country}]` : ""}
            </Typography>
          </Stack>
        ),
      },
      {
        field: "action",
        headerName: t("adminlogs.action"),
        flex: 1,
        minWidth: 220,
        renderCell: ({ row }) => (
          <Stack spacing={0.2}>
            <Typography variant="body2" sx={{ fontWeight: 500 }}>
              {getActionSummary(row, t)}
            </Typography>
            <Typography variant="caption" className="mono muted">
              {row.action}
            </Typography>
          </Stack>
        ),
      },
      {
        field: "method",
        headerName: t("adminlogs.methodPath"),
        width: 220,
        renderCell: ({ row }) => {
          let color: "default" | "success" | "warning" | "error" | "info" = "info";
          if (row.method === "POST") color = "success";
          else if (row.method === "DELETE") color = "error";
          else if (row.method === "PUT" || row.method === "PATCH") color = "warning";

          return (
            <Stack direction="row" spacing={1} sx={{ alignItems: "center" }}>
              <Chip
                label={row.method}
                size="small"
                color={color}
                variant="outlined"
                sx={{ fontFamily: "monospace", fontWeight: 700, fontSize: 11 }}
              />
              <Typography
                variant="body2"
                className="mono"
                sx={{ fontSize: 12, overflow: "hidden", textOverflow: "ellipsis" }}
              >
                {row.path}
              </Typography>
            </Stack>
          );
        },
      },
      {
        field: "ip",
        headerName: t("adminlogs.ip"),
        width: 140,
        renderCell: ({ value }) => (
          <Chip
            label={value}
            size="small"
            variant="filled"
            sx={{
              fontFamily: "monospace",
              fontSize: 12,
              background: "rgba(15, 23, 42, 0.6)",
              border: "1px solid #334155",
            }}
          />
        ),
      },
      {
        field: "status",
        headerName: t("adminlogs.status"),
        width: 130,
        renderCell: ({ row }) => {
          const isSuccess = row.status === "SUCCESS";
          return (
            <Chip
              label={`${row.status} ${row.statusCode || ""}`.trim()}
              size="small"
              color={isSuccess ? "success" : "error"}
              sx={{ fontWeight: 600, fontSize: 11 }}
            />
          );
        },
      },
      {
        field: "actions",
        headerName: "",
        width: 90,
        sortable: false,
        renderCell: ({ row }) => (
          <Button
            size="small"
            variant="text"
            color="inherit"
            onClick={() => setSelectedLog(row)}
            startIcon={<VisibilityOutlinedIcon />}
          >
            {t("adminlogs.viewDetail")}
          </Button>
        ),
      },
    ],
    [t, fmtDate]
  );

  return (
    <Stack spacing={3}>
      {/* Header */}
      <Stack
        direction={{ xs: "column", sm: "row" }}
        spacing={2}
        sx={{
          justifyContent: "space-between",
          alignItems: { xs: "flex-start", sm: "center" },
        }}
      >
        <div>
          <Typography variant="h5" component="h1" sx={{ fontWeight: 700 }}>
            {t("adminlogs.title")}
          </Typography>
          <Typography variant="body2" className="muted" sx={{ mt: 0.5 }}>
            {t("adminlogs.subtitle")}
          </Typography>
        </div>
        <Stack direction="row" spacing={1.5}>
          <Button
            variant="outlined"
            color="inherit"
            startIcon={<RefreshIcon />}
            onClick={() => reloadLogs()}
          >
            {t("common.refresh")}
          </Button>
          <Button
            variant="contained"
            color="error"
            startIcon={<DeleteSweepOutlinedIcon />}
            onClick={handleCleanLogs}
          >
            {t("adminlogs.cleanBtn")}
          </Button>
        </Stack>
      </Stack>

      {/* Filter Card */}
      <Paper variant="outlined" sx={{ p: 2 }}>
        <Stack spacing={2}>
          <Stack
            direction={{ xs: "column", sm: "row" }}
            spacing={2}
            sx={{ alignItems: "center" }}
          >
            <TextField
              size="small"
              placeholder={t("adminlogs.searchPh")}
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && handleApplyFilter()}
              sx={{ flex: 2, minWidth: 200 }}
            />
            <TextField
              size="small"
              placeholder={t("adminlogs.user")}
              value={username}
              onChange={(e) => setUsername(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && handleApplyFilter()}
              sx={{ flex: 1, minWidth: 120 }}
            />
            <TextField
              size="small"
              placeholder={t("adminlogs.ip")}
              value={ip}
              onChange={(e) => setIp(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && handleApplyFilter()}
              sx={{ flex: 1, minWidth: 130 }}
            />
            <TextField
              select
              size="small"
              value={action}
              onChange={(e) => setAction(e.target.value)}
              sx={{ flex: 1.5, minWidth: 160 }}
            >
              <MenuItem value="">-- {t("adminlogs.allActions")} --</MenuItem>
              {(actionsData || []).map((act) => {
                const actionKey = `action.${act.replace(/:/g, ".")}`;
                const label = t(actionKey as any);
                return (
                  <MenuItem key={act} value={act}>
                    {label !== actionKey ? `${act} (${label})` : act}
                  </MenuItem>
                );
              })}
            </TextField>
            <TextField
              select
              size="small"
              value={status}
              onChange={(e) => setStatus(e.target.value as any)}
              sx={{ width: 150 }}
            >
              <MenuItem value="">-- {t("adminlogs.allStatuses")} --</MenuItem>
              <MenuItem value="SUCCESS">SUCCESS</MenuItem>
              <MenuItem value="FAILED">FAILED</MenuItem>
            </TextField>
            <Button variant="contained" onClick={handleApplyFilter}>
              {t("adminlogs.filterBtn")}
            </Button>
          </Stack>
        </Stack>
      </Paper>

      {/* Data Table */}
      {logsError ? (
        <ErrorBox message={logsError} />
      ) : (
        <Paper variant="outlined" sx={{ p: 0, overflow: "hidden" }}>
          <DataTable
            rows={logsData?.items ?? []}
            columns={columns}
            loading={logsLoading}
            hideFooter
            autoHeight
          />
          {/* Pagination bar */}
          <Stack
            direction="row"
            sx={{
              justifyContent: "space-between",
              alignItems: "center",
              p: 2,
              borderTop: "1px solid #334155",
            }}
          >
            <Typography variant="body2" className="muted">
              {t("adminlogs.paginationInfo", {
                total: logsData?.total ?? 0,
                page,
                totalPages: logsData?.totalPages || 1,
              })}
            </Typography>
            <Pagination
              count={logsData?.totalPages || 1}
              page={page}
              onChange={handlePageChange}
              color="primary"
              shape="rounded"
              size="small"
            />
          </Stack>
        </Paper>
      )}

      {/* Detail Modal */}
      {selectedLog && (
        <Modal
          onClose={() => setSelectedLog(null)}
          title={`${t("adminlogs.detailTitle")} #${selectedLog.id}`}
        >
          <Stack spacing={2.5}>
            {selectedLog.errorMessage && (
              <ErrorBox message={selectedLog.errorMessage} />
            )}

            {/* Quick Grid Info */}
            <Box
              sx={{
                display: "grid",
                gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" },
                gap: 2,
                p: 2,
                bgcolor: "rgba(15, 23, 42, 0.5)",
                borderRadius: 2,
                border: "1px solid #334155",
              }}
            >
              <div>
                <Typography variant="caption" className="muted">
                  {t("adminlogs.time")}:
                </Typography>
                <Typography variant="body2" sx={{ fontWeight: 600 }}>
                  {fmtDate(selectedLog.createdAt)}
                </Typography>
              </div>
              <div>
                <Typography variant="caption" className="muted">
                  {t("adminlogs.user")}:
                </Typography>
                <Typography variant="body2" sx={{ fontWeight: 600 }}>
                  {selectedLog.username || "system"} ({selectedLog.role || "system"})
                </Typography>
              </div>
              <div>
                <Typography variant="caption" className="muted">
                  {t("adminlogs.ip")}:
                </Typography>
                <Typography
                  variant="body2"
                  className="mono"
                  sx={{ color: "primary.main", fontWeight: 700 }}
                >
                  {selectedLog.ip}
                </Typography>
              </div>
              <div>
                <Typography variant="caption" className="muted">
                  {t("adminlogs.status")}:
                </Typography>
                <Box sx={{ mt: 0.5 }}>
                  <Chip
                    label={`${selectedLog.status} (HTTP ${selectedLog.statusCode || 200})`}
                    size="small"
                    color={selectedLog.status === "SUCCESS" ? "success" : "error"}
                    sx={{ fontWeight: 700 }}
                  />
                </Box>
              </div>
              <div>
                <Typography variant="caption" className="muted">
                  {t("adminlogs.action")}:
                </Typography>
                <Typography variant="body2" className="mono" sx={{ fontWeight: 600 }}>
                  {selectedLog.action}
                </Typography>
              </div>
              <div>
                <Typography variant="caption" className="muted">
                  {t("adminlogs.description")}:
                </Typography>
                <Typography variant="body2" sx={{ fontWeight: 500 }}>
                  {getActionSummary(selectedLog, t)}
                </Typography>
              </div>
              <div>
                <Typography variant="caption" className="muted">
                  {t("adminlogs.request")}:
                </Typography>
                <Typography variant="body2" className="mono">
                  {selectedLog.method} {selectedLog.path}
                </Typography>
              </div>
              <div>
                <Typography variant="caption" className="muted">
                  {t("adminlogs.duration")}:
                </Typography>
                <Typography variant="body2">
                  {selectedLog.durationMs != null ? `${selectedLog.durationMs} ms` : "—"}
                </Typography>
              </div>
            </Box>

            {/* User-Agent */}
            <div>
              <Typography variant="caption" className="muted" sx={{ mb: 0.5, display: "block" }}>
                {t("adminlogs.userAgent")}:
              </Typography>
              <Paper
                variant="outlined"
                sx={{
                  p: 1.5,
                  bgcolor: "rgba(15, 23, 42, 0.4)",
                  fontFamily: "monospace",
                  fontSize: 12,
                  wordBreak: "break-all",
                }}
              >
                {selectedLog.userAgent || "—"}
              </Paper>
            </div>

            {/* Request Body Payload */}
            <div>
              <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 0.5 }}>
                {t("adminlogs.payloadBody")}:
              </Typography>
              <Paper
                variant="outlined"
                sx={{
                  p: 2,
                  bgcolor: "#090d16",
                  maxHeight: 260,
                  overflow: "auto",
                }}
              >
                <pre
                  style={{
                    margin: 0,
                    fontFamily: "ui-monospace, monospace",
                    fontSize: 12,
                    color: "#a7f3d0",
                  }}
                >
                  {selectedLog.body ? JSON.stringify(selectedLog.body, null, 2) : "null"}
                </pre>
              </Paper>
            </div>

            {/* Params */}
            {selectedLog.params && (
              <div>
                <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 0.5 }}>
                  {t("adminlogs.params")}:
                </Typography>
                <Paper
                  variant="outlined"
                  sx={{
                    p: 1.5,
                    bgcolor: "#090d16",
                    maxHeight: 160,
                    overflow: "auto",
                  }}
                >
                  <pre
                    style={{
                      margin: 0,
                      fontFamily: "ui-monospace, monospace",
                      fontSize: 12,
                      color: "#93c5fd",
                    }}
                  >
                    {JSON.stringify(selectedLog.params, null, 2)}
                  </pre>
                </Paper>
              </div>
            )}
          </Stack>
        </Modal>
      )}
    </Stack>
  );
}
